(() => {
  'use strict';

  if (document.body?.dataset.productHandle !== 'advanced-anti-snoring-device-4-0') return;
  const main = document.getElementById('MainContent');
  if (!main || window.DifineyPdpMotion) return;

  // Explicit visual groups only. Never animate a section root, purchase form,
  // app widget, sticky media wrapper, carousel card, or the comparison slider.
  const riseTargets = [
    '.why-stands-out__header',
    '.why-stands-out__feature',
    '.why-stands-out__card',
    '.dif-edu-mechanism__heading',
    '.dif-edu-mechanism__media .dif-edu-video__frame',
    '.dif-edu-mechanism__steps .dif-edu-step',
    '.dif-mold__copy > .dif-edu-section__heading',
    '.dif-mold__remold',
    '.dif-mold__media .dif-edu-video__frame',
    '.dif-adjustment__heading',
    '.dif-adjustment__video-frame',
    '.dif-adjustment__controls',
    '.reviewed-by-pros__head',
    '.reviewed-by-pros__card',
    '.comparison-table__heading',
    '.final-cta__content',
    '.dfy-ugc__header',
    '.faq-section__header',
    '.faq-section__content'
  ].join(',');
  const fadeTargets = '.comparison-table__grid--header, .comparison-table__grid--row, .dfy-ugc__stage-wrap';
  const targets = `${riseTargets},${fadeTargets}`;
  const excluded = '.shopify-app-block, .shopify-block, [id^="shopify-block"], .hero-section, .mobile-sticky-cta, form, [hidden], [inert], [aria-hidden="true"]';
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controller = new AbortController();
  const seen = new WeakSet();
  const pending = new Map();
  const playing = new Map();
  let observer = null;
  let initialFrame = null;

  const editorMode = () => Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
  const enabled = () => !query.matches && !editorMode() && 'IntersectionObserver' in window;
  const visible = (node) => {
    if (!node.isConnected || node.closest(excluded) || !node.getClientRects().length) return false;
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.position !== 'fixed' && style.position !== 'sticky';
  };

  function finish(node) {
    const timer = playing.get(node);
    if (timer !== undefined) window.clearTimeout(timer);
    playing.delete(node);
    node.classList.remove('dfy-pdp-enter');
    node.style.removeProperty('--dfy-pdp-delay');
    delete node.dataset.pdpMotion;
  }

  function reveal(node, options) {
    if (!enabled() || !visible(node)) return;
    seen.add(node);
    // No pending/hidden class: entrance starts only after the node intersects.
    node.dataset.pdpMotion = options.fade ? 'fade' : 'rise';
    node.style.setProperty('--dfy-pdp-delay', `${options.delay}ms`);
    node.classList.add('dfy-pdp-enter');
    playing.set(node, window.setTimeout(() => finish(node), options.delay + 560));
  }

  function getObserver() {
    if (!observer) {
      observer = new IntersectionObserver((entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const options = pending.get(entry.target);
          currentObserver.unobserve(entry.target);
          pending.delete(entry.target);
          if (options) reveal(entry.target, options);
        });
      }, { threshold: .08, rootMargin: '0px 0px -24px 0px' });
    }
    return observer;
  }

  function register(scope = main) {
    if (!enabled()) return;
    const nodes = Array.from(scope.querySelectorAll(targets));
    if (scope.matches?.(targets)) nodes.unshift(scope);
    const sectionIndexes = new Map();
    nodes.forEach((node) => {
      if (!main.contains(node) || seen.has(node) || pending.has(node) || !visible(node)) return;
      const section = node.closest('.shopify-section') || main;
      const index = sectionIndexes.get(section) || 0;
      sectionIndexes.set(section, index + 1);
      const rect = node.getBoundingClientRect();
      // Keep the initial viewport and scroll-restored content steady.
      if (rect.top < window.innerHeight) {
        seen.add(node);
        return;
      }
      pending.set(node, { fade: node.matches(fadeTargets), delay: Math.min(index, 3) * 60 });
      getObserver().observe(node);
    });
  }

  function clearWithin(scope) {
    pending.forEach((_, node) => {
      if (scope === node || scope.contains(node)) {
        observer?.unobserve(node);
        pending.delete(node);
      }
    });
    Array.from(playing.keys()).forEach((node) => {
      if (scope === node || scope.contains(node)) finish(node);
    });
  }

  function refresh() {
    observer?.disconnect();
    observer = null;
    pending.clear();
    Array.from(playing.keys()).forEach(finish);
    register();
  }

  main.addEventListener('animationend', (event) => {
    if (event.animationName === 'dfy-pdp-enter' || event.animationName === 'dfy-pdp-fade') finish(event.target);
  }, { signal: controller.signal });
  main.addEventListener('focusin', (event) => {
    const node = event.target.closest('.dfy-pdp-enter');
    if (node) finish(node);
  }, { signal: controller.signal });
  document.addEventListener('shopify:section:load', (event) => register(event.target), { signal: controller.signal });
  document.addEventListener('shopify:section:unload', (event) => clearWithin(event.target), { signal: controller.signal });
  query.addEventListener('change', refresh);

  window.DifineyPdpMotion = {
    refresh,
    destroy() {
      if (initialFrame !== null) window.cancelAnimationFrame(initialFrame);
      observer?.disconnect();
      pending.clear();
      Array.from(playing.keys()).forEach(finish);
      controller.abort();
      query.removeEventListener('change', refresh);
      delete window.DifineyPdpMotion;
    }
  };

  initialFrame = window.requestAnimationFrame(() => {
    initialFrame = null;
    register();
  });
})();
